import { useContext, useEffect, useRef, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X, Send } from "lucide-react";
import { ContentCtx } from "@/lib/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CONV_KEY = "stax_chat_id";

export const ChatWidget = () => {
  const content = useContext(ContentCtx);
  const enabled = content?.chat?.enabled !== false;
  const [open, setOpen] = useState(false);
  const [convId, setConvId] = useState(() => localStorage.getItem(CONV_KEY) || "");
  const [thread, setThread] = useState(null);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    if (!enabled || !convId) return;
    let cancelled = false;
    const load = async () => {
      try {
        const r = await axios.get(`${API}/chat/${convId}`, { params: { peek: open ? 0 : 1 } });
        if (!cancelled) setThread(r.data);
      } catch {
        if (!cancelled) {
          localStorage.removeItem(CONV_KEY);
          setConvId("");
          setThread(null);
        }
      }
    };
    load();
    const t = setInterval(load, 8000);
    return () => { cancelled = true; clearInterval(t); };
  }, [enabled, convId, open]);

  useEffect(() => {
    if (open) bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [open, thread?.messages?.length]);

  if (!enabled) return null;

  const start = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await axios.post(`${API}/chat/start`, { name, email });
      localStorage.setItem(CONV_KEY, r.data.id);
      setConvId(r.data.id);
    } catch {
      // chat offline mid-click
    } finally {
      setBusy(false);
    }
  };

  const send = async () => {
    if (!draft.trim() || !convId) return;
    const text = draft.trim();
    setDraft("");
    setThread((t) => t && ({
      ...t,
      messages: [...t.messages, { id: `tmp-${Date.now()}`, sender: "visitor", text, ts: new Date().toISOString() }],
    }));
    try {
      await axios.post(`${API}/chat/${convId}/messages`, { text });
    } catch { /* next poll reconciles */ }
  };

  const unread = thread?.chat?.unread_visitor || 0;

  return (
    <>
      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="chat-panel"
            initial={{ opacity: 0, y: 24, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 24, scale: 0.96 }}
            transition={{ duration: 0.22 }}
            className="fixed bottom-24 right-4 sm:right-6 z-40 flex h-[460px] w-[calc(100vw-2rem)] max-w-sm flex-col border-4 border-ink bg-paper shadow-hard"
          >
            <div className="flex items-center justify-between border-b-4 border-ink bg-staxmagenta px-4 py-3">
              <div>
                <p className="font-display uppercase text-lg text-white leading-none">Chat with STAX</p>
                <p className="font-mono2 text-[9px] uppercase tracking-[0.2em] text-white/70 mt-1">Replies within a few hours</p>
              </div>
              <button data-testid="chat-close-button" onClick={() => setOpen(false)} aria-label="Close chat"
                className="border-2 border-white bg-transparent p-1 text-white hover:bg-white hover:text-ink transition-colors">
                <X size={16} />
              </button>
            </div>

            {!convId ? (
              <form onSubmit={start} className="flex flex-1 flex-col justify-center gap-3 p-5">
                <p className="font-display uppercase text-xl text-ink">Need a quote?</p>
                <p className="text-sm font-medium text-ink/60 leading-relaxed">
                  Drop your name and question — I usually reply within a few minutes to a few hours.
                </p>
                <input data-testid="chat-start-name" value={name} onChange={(e) => setName(e.target.value)}
                  placeholder="Your name"
                  className="border-2 border-ink bg-white px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-staxmagenta" />
                <input data-testid="chat-start-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="Email (optional)"
                  className="border-2 border-ink bg-white px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-staxmagenta" />
                <button data-testid="chat-start-button" type="submit" disabled={busy}
                  className="border-2 border-ink bg-staxyellow px-5 py-3 font-display uppercase text-ink shadow-hard-sm transition-transform hover:-translate-y-0.5 disabled:opacity-60">
                  {busy ? "Starting..." : "Start Chat"}
                </button>
              </form>
            ) : (
              <>
                <div data-testid="chat-messages" data-lenis-prevent className="flex-1 space-y-2 overflow-y-auto p-4">
                  {thread?.messages?.map((m) => (
                    <div key={m.id} className={`flex ${m.sender === "visitor" ? "justify-end" : "justify-start"}`}>
                      <div className={`max-w-[85%] border-2 border-ink px-3 py-2 text-sm ${
                        m.sender === "visitor" ? "bg-staxcyan text-ink" : "bg-white text-ink"
                      }`}>
                        {m.text}
                      </div>
                    </div>
                  ))}
                  <div ref={bottomRef} />
                </div>
                <div className="flex gap-2 border-t-4 border-ink bg-white p-3">
                  <input
                    data-testid="chat-message-input"
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && send()}
                    placeholder="Type a message..."
                    className="flex-1 border-2 border-ink bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-staxmagenta"
                  />
                  <button data-testid="chat-message-send" onClick={send} aria-label="Send message"
                    className="border-2 border-ink bg-staxmagenta px-3.5 text-white transition-transform hover:-translate-y-0.5">
                    <Send size={16} />
                  </button>
                </div>
              </>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      <button
        data-testid="chat-widget-button"
        onClick={() => setOpen((o) => !o)}
        aria-label="Open chat"
        className="fixed bottom-5 right-4 sm:right-6 z-40 flex h-14 w-14 items-center justify-center rounded-full border-4 border-ink bg-staxyellow text-ink shadow-hard transition-transform hover:-translate-y-1"
      >
        {open ? <X size={22} /> : <MessageCircle size={22} />}
        {!open && unread > 0 && (
          <span data-testid="chat-widget-badge" className="absolute -right-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full border-2 border-ink bg-staxmagenta px-1 font-mono2 text-[10px] font-bold text-white">
            {unread}
          </span>
        )}
      </button>
    </>
  );
};
